import { useMemo } from 'react'
import { useLayerView } from '@/api/hooks/useLayerView'
import { useMetadata } from '@/api/hooks/useMetadata'
import { useGlobalStore } from '@/stores/globalStore'
import { useNavigationStore } from '@/stores/navigationStore'
import { useSettingsSelector } from '@/utils'
import { useNavigationDemo } from '@/utils/settings/use-navigation-demo'
import {
  buildRouteFromKeypoints,
  buildRouteFromMockKeypoints,
} from './routePathfinding'

const useRouteEndpoints = () =>
  useNavigationStore((state) => {
    if (state.isRouteActive && state.route.from && state.route.to) {
      return { from: state.route.from, to: state.route.to }
    }

    if (state.selected.from && state.selected.to) {
      return { from: state.selected.from, to: state.selected.to }
    }

    return { from: null, to: null }
  })

export const useRoute = () => {
  const { from, to } = useRouteEndpoints()
  const activeLayer = useGlobalStore((state) => state.activeLayer)
  const navigationDemo = useNavigationDemo()
  const keypointConfigs = useSettingsSelector(
    (settings) => settings.navigation?.keypoint ?? [],
    [],
  )
  const { layerView } = useLayerView(activeLayer)
  const { metadata } = useMetadata()

  return useMemo(() => {
    if (!from?.x || !from?.y || !to?.x || !to?.y) return [] as [number, number][]

    const graphRoute = buildRouteFromKeypoints({
      from,
      to,
      layerPoints: layerView?.points ?? [],
      keypointConfigs,
      metadataNodes: metadata?.nodes ?? [],
    })

    if (graphRoute?.length) {
      return graphRoute
    }

    const demoLayerId = navigationDemo?.layerId
    const isDemoLayer =
      demoLayerId != null &&
      from.layerId === demoLayerId &&
      to.layerId === demoLayerId

    const mockRoute = navigationDemo?.mockRoute

    if (isDemoLayer && mockRoute?.length) {
      return mockRoute.map(([x, y]) => [x, y] as [number, number])
    }

    if (isDemoLayer && navigationDemo?.mockKeypoints?.length) {
      const mockGraphRoute = buildRouteFromMockKeypoints({
        from,
        to,
        mockKeypoints: navigationDemo.mockKeypoints,
      })

      if (mockGraphRoute?.length) {
        return mockGraphRoute
      }
    }

    return [
      [from.x, from.y],
      [to.x, to.y],
    ] as [number, number][]
  }, [
    from,
    to,
    layerView?.points,
    keypointConfigs,
    metadata?.nodes,
    navigationDemo,
  ])
}
